import React, { useState, useEffect } from 'react';
import { FileText, Download, Edit, Plus, X, Check, Send, Scale, Users } from 'lucide-react';
import { toast } from 'sonner';
import DocumentEditor from './DocumentEditor';
import { templatesAPI, casesAPI } from '../utils/api';

const TemplateLibrary = ({ cases, referrals }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [templateToAssign, setTemplateToAssign] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/templates/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.length === 0) {
          // Seed default templates if none exist
          await seedDefaultTemplates();
          return;
        }
        setTemplates(data);
      } else {
        // Seed default templates
        await seedDefaultTemplates();
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      // Show empty state
      setTemplates([]);
    }
  };

  const seedDefaultTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/templates/seed-defaults`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      // Reload templates after seeding
      const response = await fetch(`${API_URL}/api/templates/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Failed to seed templates:', error);
    }
  };

  const loadDefaultTemplates_OLD = () => {
    // This function is no longer used - templates are loaded from backend
    const defaultTemplates = [
      {
        id: 'engagement-letter',
        name: 'Client Engagement Letter',
        category: 'Engagement',
        description: 'Initial engagement agreement for reserve recovery services',
        content: `<div class="document-template">
  <div class="header">
    <h1>RESERVE RELEASE AI, LLC</h1>
    <p>1804 Garnet Ave, San Diego, CA 92109</p>
    <p>www.reserverelease.AI</p>
  </div>
  
  <h2 style="text-align: center; margin: 30px 0;">CLIENT ENGAGEMENT AGREEMENT</h2>
  
  <p><strong>Date:</strong> {{current_date}}</p>
  
  <p><strong>Client Name:</strong> {{client_name}}<br/>
  <strong>Company:</strong> {{company_name}}<br/>
  <strong>Email:</strong> {{client_email}}<br/>
  <strong>Phone:</strong> {{client_phone}}</p>
  
  <h3>RE: Recovery of Held Reserves</h3>
  
  <p>Dear {{client_name}},</p>
  
  <p>This letter confirms our engagement to represent you in the recovery of held reserves from <strong>{{processor_name}}</strong> in the approximate amount of <strong>${{frozen_amount}}</strong>.</p>
  
  <h3>TERMS OF ENGAGEMENT</h3>
  
  <p><strong>1. Services:</strong> reserverelease.AI, LLC will utilize our specialized legal expertise and AI-driven recovery model to negotiate and recover your held reserves from the merchant bank.</p>
  
  <p><strong>2. Fee Structure:</strong> Our fee is <strong>{{fee_percentage}}%</strong> of the gross recovered funds. This fee is contingent upon successful recovery - if we do not recover any funds, you owe nothing.</p>
  
  <p><strong>3. Payment Terms:</strong> Recovered funds will be distributed as follows:</p>
  <ul>
    <li>reserverelease.AI fee: {{fee_percentage}}% of recovered amount</li>
    <li>Client receives: {{client_percentage}}% of recovered amount</li>
    <li>Payment to client within 15 business days of fund receipt</li>
  </ul>
  
  <p><strong>4. Timeline:</strong> We estimate the recovery process will take 90-180 days, though this may vary based on bank cooperation and case complexity.</p>
  
  <p><strong>5. Client Responsibilities:</strong></p>
  <ul>
    <li>Provide all requested documentation promptly</li>
    <li>Respond to communications within 48 hours</li>
    <li>Maintain confidentiality of our proprietary methods</li>
  </ul>
  
  <h3>AUTHORIZATION</h3>
  
  <p>By signing below, you authorize reserverelease.AI, LLC to act on your behalf in all matters related to the recovery of your held reserves.</p>
  
  <div class="signature-block" style="margin-top: 50px;">
    <div style="display: flex; justify-content: space-between;">
      <div style="width: 45%;">
        <div class="signature-line" style="border-top: 2px solid #000; padding-top: 10px; margin-top: 60px;">
          <p><strong>Client Signature</strong></p>
          <p>{{client_name}}</p>
          <p>Date: _________________</p>
        </div>
      </div>
      <div style="width: 45%;">
        <div class="signature-line" style="border-top: 2px solid #000; padding-top: 10px; margin-top: 60px;">
          <p><strong>reserverelease.AI, LLC</strong></p>
          <p>Authorized Representative</p>
          <p>Date: {{current_date}}</p>
        </div>
      </div>
    </div>
  </div>
</div>`
      },
      {
        id: 'bank-demand-letter',
        name: 'Bank Demand Letter',
        category: 'Legal',
        description: 'Formal demand letter to merchant bank for reserve release',
        content: `<div class="document-template">
  <div class="header">
    <h1>RESERVE RELEASE AI, LLC</h1>
    <p>Legal Department</p>
    <p>1804 Garnet Ave, San Diego, CA 92109</p>
  </div>
  
  <p style="margin-top: 30px;">{{current_date}}</p>
  
  <p><strong>{{bank_name}}</strong><br/>
  {{bank_address}}<br/>
  {{bank_phone}}</p>
  
  <p><strong>RE: Demand for Release of Held Reserves</strong><br/>
  <strong>Account Holder:</strong> {{company_name}}<br/>
  <strong>Processor:</strong> {{processor_name}}<br/>
  <strong>Amount:</strong> ${{frozen_amount}}</p>
  
  <p>Dear Sir/Madam,</p>
  
  <p>This office represents {{company_name}} ("Client") regarding held reserves in the approximate amount of <strong>${{frozen_amount}}</strong> currently retained by your institution through {{processor_name}}.</p>
  
  <p><strong>BACKGROUND:</strong></p>
  <p>Our client's merchant account was closed on or about {{freeze_date}}, and funds have been held without proper justification or reasonable timeframe for release. The account was in good standing with no outstanding disputes or chargebacks warranting this extended hold period.</p>
  
  <p><strong>LEGAL BASIS FOR RELEASE:</strong></p>
  <p>Under applicable merchant processing agreements and federal banking regulations, held reserves must be released within a reasonable timeframe once the risk period has expired. The current hold period of {{days_frozen}} days exceeds industry standards and constitutes an improper retention of funds.</p>
  
  <p><strong>DEMAND:</strong></p>
  <p>We hereby formally demand the immediate release of all held reserves in the amount of ${{frozen_amount}} to our client. We request confirmation of release within <strong>15 business days</strong> of receipt of this letter.</p>
  
  <p><strong>PROPOSED RESOLUTION:</strong></p>
  <p>We propose the following resolution to avoid escalation:</p>
  <ul>
    <li>Immediate release of {{release_percentage}}% of held reserves (${{release_amount}})</li>
    <li>Remaining funds released within 30 days after dispute resolution period</li>
    <li>Written confirmation of release timeline</li>
  </ul>
  
  <p>Should you fail to respond or refuse to release these funds, we are prepared to pursue all available legal remedies, including but not limited to formal arbitration proceedings and regulatory complaints.</p>
  
  <p>Please direct all correspondence to our office. We look forward to your prompt response.</p>
  
  <p style="margin-top: 40px;">Respectfully,</p>
  
  <div class="signature-line" style="margin-top: 60px;">
    <p><strong>reserverelease.AI, LLC</strong><br/>
    Legal Department<br/>
    Email: legal@reserverelease.AI<br/>
    Phone: (858) 555-0100</p>
  </div>
  
  <p style="margin-top: 30px; font-size: 12px;"><strong>CC:</strong> {{client_name}}, Client</p>
</div>`
      },
      {
        id: 'settlement-agreement',
        name: 'Settlement Agreement',
        category: 'Legal',
        description: 'Settlement agreement for negotiated reserve release',
        content: `<div class="document-template">
  <h1 style="text-align: center;">SETTLEMENT AGREEMENT AND RELEASE</h1>
  
  <p style="text-align: center; margin-bottom: 30px;">Date: {{current_date}}</p>
  
  <p>This Settlement Agreement ("Agreement") is entered into by and between:</p>
  
  <p><strong>PARTY A ("Merchant"):</strong><br/>
  {{company_name}}<br/>
  {{client_name}}<br/>
  Represented by: reserverelease.AI, LLC</p>
  
  <p><strong>PARTY B ("Bank"):</strong><br/>
  {{bank_name}}<br/>
  {{bank_address}}</p>
  
  <h3>RECITALS</h3>
  
  <p>WHEREAS, Merchant maintained a merchant processing account with {{processor_name}}, which was closed on {{freeze_date}};</p>
  
  <p>WHEREAS, Bank has held reserves in the amount of ${{frozen_amount}} ("Held Reserves");</p>
  
  <p>WHEREAS, the parties wish to resolve all disputes regarding the Held Reserves;</p>
  
  <p>NOW, THEREFORE, in consideration of the mutual covenants contained herein, the parties agree as follows:</p>
  
  <h3>TERMS OF SETTLEMENT</h3>
  
  <p><strong>1. Release of Funds:</strong> Bank agrees to release <strong>{{settlement_percentage}}%</strong> of the Held Reserves, totaling <strong>${{settlement_amount}}</strong>, to Merchant within <strong>15 business days</strong> of execution of this Agreement.</p>
  
  <p><strong>2. Payment Method:</strong> Funds shall be wired to the account specified by Merchant's representative, reserverelease.AI, LLC.</p>
  
  <p><strong>3. Retained Amount:</strong> Bank shall retain <strong>{{retained_percentage}}%</strong> (${{retained_amount}}) as reserve for potential chargebacks for a period of <strong>{{retention_days}} days</strong> from the date of this Agreement.</p>
  
  <p><strong>4. Final Release:</strong> Any remaining funds after the retention period, minus legitimate chargebacks, shall be released to Merchant within 10 business days of expiration of the retention period.</p>
  
  <p><strong>5. Mutual Release:</strong> Upon payment of the settlement amount, both parties release each other from all claims related to the merchant account and Held Reserves.</p>
  
  <p><strong>6. Confidentiality:</strong> The terms of this Settlement Agreement shall remain confidential.</p>
  
  <p><strong>7. Governing Law:</strong> This Agreement shall be governed by the laws of the State of California.</p>
  
  <div class="signature-block" style="margin-top: 60px;">
    <div style="display: flex; justify-content: space-between;">
      <div style="width: 45%;">
        <div class="signature-line" style="border-top: 2px solid #000; padding-top: 10px; margin-top: 60px;">
          <p><strong>MERCHANT</strong></p>
          <p>{{company_name}}</p>
          <p>By: _________________</p>
          <p>Name: {{client_name}}</p>
          <p>Date: _________________</p>
        </div>
      </div>
      <div style="width: 45%;">
        <div class="signature-line" style="border-top: 2px solid #000; padding-top: 10px; margin-top: 60px;">
          <p><strong>BANK</strong></p>
          <p>{{bank_name}}</p>
          <p>By: _________________</p>
          <p>Name: _________________</p>
          <p>Date: _________________</p>
        </div>
      </div>
    </div>
  </div>
  
  <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #ccc;">
    <p><strong>APPROVED BY REPRESENTATIVE:</strong></p>
    <div class="signature-line" style="margin-top: 30px;">
      <p>reserverelease.AI, LLC<br/>
      Authorized Representative<br/>
      Date: {{current_date}}</p>
    </div>
  </div>
</div>`
      },
      {
        id: 'status-report',
        name: 'Client Status Report',
        category: 'Communication',
        description: 'Weekly status update for clients',
        content: `<div class="document-template">
  <div class="header" style="background: linear-gradient(135deg, #7c3aed 0%, #06b6d4 100%); padding: 20px; color: white;">
    <h1>reserverelease.AI</h1>
    <p>Case Status Report</p>
  </div>
  
  <div style="padding: 20px;">
    <p><strong>Report Date:</strong> {{current_date}}<br/>
    <strong>Case ID:</strong> {{case_id}}</p>
    
    <h2>Client Information</h2>
    <p><strong>Name:</strong> {{client_name}}<br/>
    <strong>Company:</strong> {{company_name}}<br/>
    <strong>Email:</strong> {{client_email}}</p>
    
    <h2>Case Details</h2>
    <p><strong>Processor:</strong> {{processor_name}}<br/>
    <strong>Bank:</strong> {{bank_name}}<br/>
    <strong>Held Reserves:</strong> ${{frozen_amount}}<br/>
    <strong>Freeze Date:</strong> {{freeze_date}}<br/>
    <strong>Days Frozen:</strong> {{days_frozen}} days</p>
    
    <h2>Current Status: {{case_status}}</h2>
    
    <h3>Recent Activity</h3>
    <ul>
      <li>Initial demand letter sent to {{bank_name}} on {{demand_date}}</li>
      <li>Follow-up communication with bank legal department</li>
      <li>Document review and case analysis completed</li>
      <li>Negotiation strategy developed</li>
    </ul>
    
    <h3>Next Steps</h3>
    <ul>
      <li>Schedule conference call with bank representatives</li>
      <li>Present settlement proposal</li>
      <li>Expected timeline for resolution: {{estimated_timeline}} days</li>
    </ul>
    
    <h3>Estimated Recovery</h3>
    <p>Based on current negotiations, we estimate recovering <strong>{{recovery_percentage}}%</strong> of held reserves, approximately <strong>${{estimated_recovery}}</strong>.</p>
    
    <div style="background: #f0fdf4; border-left: 4px solid #22c55e; padding: 15px; margin: 20px 0;">
      <h4 style="margin-top: 0; color: #166534;">Good News!</h4>
      <p style="margin-bottom: 0; color: #166534;">Bank has responded positively to our demand letter and expressed willingness to negotiate release terms.</p>
    </div>
    
    <h3>Questions or Concerns?</h3>
    <p>Please don't hesitate to contact us:<br/>
    Email: support@reserverelease.AI<br/>
    Phone: (858) 555-0100</p>
    
    <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
      <p style="font-size: 12px; color: #6b7280;">This report is confidential and intended solely for the use of {{client_name}}. Please do not share without permission from reserverelease.AI, LLC.</p>
    </div>
  </div>
</div>`
      }
    ];
    
    setTemplates(defaultTemplates);
  };

  const handleCreateDocument = (template) => {
    setSelectedTemplate(template);
    setShowEditor(true);
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setSelectedTemplate(null);
  };

  const handleAssignTemplate = (template) => {
    setTemplateToAssign(template);
    setShowAssignModal(true);
  };

  const handleAssignToClient = async (caseId, clientId) => {
    try {
      await templatesAPI.assignTemplate(templateToAssign.id, caseId, clientId);
      toast.success(`${templateToAssign.name} assigned to client successfully!`);
      setShowAssignModal(false);
      setTemplateToAssign(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign template');
    }
  };

  return (
    <div>
      {!showEditor ? (
        <div>
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Document Templates</h2>
            <p className="text-slate-600">Create professional documents with auto-filled case and referral data</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <div
                key={template.id}
                className={`border rounded-lg p-6 hover:shadow-lg transition-all ${
                  template.template_type === 'poa' || template.id === 'limited-poa'
                    ? 'border-purple-300 bg-purple-50'
                    : 'border-slate-200 hover:border-purple-300'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    template.template_type === 'poa' || template.id === 'limited-poa'
                      ? 'bg-purple-600'
                      : 'bg-purple-100'
                  }`}>
                    {template.template_type === 'poa' || template.id === 'limited-poa' ? (
                      <Scale className="text-white" size={24} />
                    ) : (
                      <FileText className="text-purple-600" size={24} />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-slate-900">{template.name}</h3>
                      {template.is_required && (
                        <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded font-medium">
                          Required
                        </span>
                      )}
                    </div>
                    <span className={`inline-block px-2 py-1 text-xs rounded mb-2 ${
                      template.category === 'Legal' 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-cyan-100 text-cyan-700'
                    }`}>
                      {template.category}
                    </span>
                    <p className="text-sm text-slate-600">{template.description}</p>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <button
                    className="flex-1 primary-button py-2 text-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreateDocument(template);
                    }}
                  >
                    Create Document
                  </button>
                  <button
                    className="px-4 py-2 border border-purple-300 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-100 transition-colors flex items-center gap-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAssignTemplate(template);
                    }}
                    title="Assign to Client"
                  >
                    <Send size={16} />
                    Assign
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Info Card */}
          <div className="mt-8 bg-purple-50 border border-purple-200 rounded-lg p-6">
            <h3 className="font-bold text-slate-900 mb-3">How to Use Templates</h3>
            <ol className="list-decimal pl-5 space-y-2 text-sm text-slate-700">
              <li>Select a template from the library above</li>
              <li>Drag and drop case or referral cards from the left panel into the document</li>
              <li>Edit percentages, dates, and custom terms as needed</li>
              <li>Capture client e-signature for approval</li>
              <li>Download as PDF or send directly to client</li>
            </ol>
          </div>
        </div>
      ) : (
        <DocumentEditor
          template={selectedTemplate}
          cases={cases}
          referrals={referrals}
          onClose={handleCloseEditor}
        />
      )}

      {/* Assign Template Modal */}
      {showAssignModal && templateToAssign && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Assign Template to Client</h2>
                <p className="text-sm text-slate-600">Select a case to assign "{templateToAssign.name}"</p>
              </div>
              <button 
                onClick={() => {
                  setShowAssignModal(false);
                  setTemplateToAssign(null);
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6">
              {cases && cases.length > 0 ? (
                <div className="space-y-3">
                  {cases.map((caseItem) => (
                    <div
                      key={caseItem.id}
                      className="border border-slate-200 rounded-lg p-4 hover:border-purple-300 hover:bg-purple-50 transition-all cursor-pointer"
                      onClick={() => handleAssignToClient(caseItem.id, caseItem.client_id)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                            <Users className="text-slate-600" size={20} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-900">{caseItem.company_name}</h4>
                            <p className="text-sm text-slate-600">{caseItem.client_name} • {caseItem.client_email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-slate-900">${caseItem.frozen_amount?.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">{caseItem.processor_name}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Users className="mx-auto text-slate-300 mb-3" size={48} />
                  <p>No cases available to assign templates to</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateLibrary;
